import { A_DOLLAR } from "./money.ts";

export const THINKING = ["off", "on", "low", "medium", "high", "max"] as const;
export type Thinking = (typeof THINKING)[number];

export type Effort = Exclude<Thinking, "off" | "on">;

export type AgentModel = {
  id: string;
  provider: string;

  mode: "never" | "either" | "always";

  efforts: readonly Effort[];

  settles: Thinking;

  price: { in: number; out: number; seen: string };
};

export type Tokens = { in: number; out: number; cacheRead: number; cacheWrite: number };

const claude: Pick<AgentModel, "provider" | "mode" | "efforts" | "settles"> = {
  provider: "anthropic",
  mode: "either",
  efforts: ["low", "medium", "high"],
  settles: "medium",
};

const codex: Pick<AgentModel, "provider" | "mode" | "efforts" | "settles"> = {
  provider: "openai-codex",
  mode: "either",
  efforts: ["low", "medium", "high"],
  settles: "medium",
};
const codexMax: Pick<AgentModel, "provider" | "mode" | "efforts" | "settles"> = {
  ...codex,
  efforts: ["low", "medium", "high", "max"],
};

const MODELS: readonly AgentModel[] = [
  {
    id: "claude-fable-5",
    provider: "anthropic",
    mode: "always",
    efforts: ["low", "medium", "high", "max"],
    settles: "high",

    price: { in: 10, out: 50, seen: "2026-08-11" },
  },
  { id: "claude-opus-5", ...claude, price: { in: 5, out: 25, seen: "2026-08-10" } },

  { id: "claude-sonnet-5", ...claude, price: { in: 3, out: 15, seen: "2026-08-10" } },
  { id: "claude-haiku-4-5", ...claude, price: { in: 1, out: 5, seen: "2026-08-10" } },

  {
    id: "zai-org-glm-5-2",
    provider: "venice",
    mode: "either",
    efforts: ["high", "max"],
    settles: "max",
    price: { in: 1.4, out: 4.4, seen: "2026-08-10" },
  },
  {
    id: "grok-4-3",
    provider: "venice",
    mode: "either",
    efforts: ["low", "medium", "high"],
    settles: "low",
    price: { in: 1.42, out: 2.83, seen: "2026-08-10" },
  },

  {
    id: "grok-4-5",
    provider: "venice",
    mode: "always",
    efforts: ["low", "medium", "high"],
    settles: "high",
    price: { in: 2.27, out: 6.8, seen: "2026-08-10" },
  },

  {
    id: "kimi-k3",
    provider: "venice",
    mode: "always",
    efforts: [],
    settles: "on",
    price: { in: 3.75, out: 18.75, seen: "2026-08-10" },
  },
  {
    id: "deepseek-v4-flash-0731",
    provider: "venice",
    mode: "either",
    efforts: ["low", "high", "max"],
    settles: "high",
    price: { in: 0.175, out: 0.35, seen: "2026-08-10" },
  },

  {
    id: "openai-gpt-56-luna",
    provider: "venice",
    mode: "either",
    efforts: ["low", "medium", "high", "max"],
    settles: "high",
    price: { in: 0.26666667, out: 1.6, seen: "2026-08-11" },
  },

  {
    id: "openai-gpt-56-sol",
    provider: "venice",
    mode: "either",
    efforts: ["low", "medium", "high", "max"],
    settles: "high",
    price: { in: 6.25, out: 37.5, seen: "2026-08-11" },
  },

  {
    id: "claude-opus-5",
    provider: "venice",
    mode: "always",
    efforts: [],
    settles: "on",
    price: { in: 6, out: 30, seen: "2026-08-11" },
  },
  {
    id: "claude-fable-5",
    provider: "venice",
    mode: "always",
    efforts: ["low", "medium", "high", "max"],
    settles: "high",
    price: { in: 12, out: 60, seen: "2026-08-11" },
  },

  {
    id: "gpt-5.6-sol",
    provider: "openai",
    mode: "either",
    efforts: ["low", "medium", "high", "max"],
    settles: "medium",

    price: { in: 5, out: 30, seen: "2026-08-11" },
  },

  {
    id: "gpt-5.6-luna",
    provider: "openai",
    mode: "either",
    efforts: ["low", "medium", "high", "max"],
    settles: "medium",

    price: { in: 0.2, out: 1.2, seen: "2026-08-11" },
  },

  {
    id: "gpt-5.3-codex-spark",
    ...codex,
    price: { in: 1.75, out: 14, seen: "2026-08-14" },
  },
  {
    id: "gpt-5.4",
    ...codex,
    price: { in: 2.5, out: 15, seen: "2026-08-14" },
  },
  {
    id: "gpt-5.4-mini",
    ...codex,
    price: { in: 0.75, out: 4.5, seen: "2026-08-14" },
  },
  {
    id: "gpt-5.5",
    ...codex,
    price: { in: 5, out: 30, seen: "2026-08-14" },
  },
  {
    id: "gpt-5.6-luna",
    ...codexMax,
    price: { in: 0.2, out: 1.2, seen: "2026-08-14" },
  },
  {
    id: "gpt-5.6-sol",
    ...codexMax,
    price: { in: 5, out: 30, seen: "2026-08-14" },
  },
  {
    id: "gpt-5.6-terra",
    ...codexMax,
    price: { in: 2, out: 12, seen: "2026-08-14" },
  },
  {
    id: "gpt-6-astra",
    ...codex,
    price: { in: 10, out: 50, seen: "2026-09-05" },
  },
];

export function modelOf(id: string, provider?: string): AgentModel | undefined {
  if (provider !== undefined) {
    return MODELS.find((model) => model.id === id && model.provider === provider);
  }
  return MODELS.find((model) => model.id === id);
}

export function modelsOfProvider(provider: string): readonly AgentModel[] {
  return MODELS.filter((model) => model.provider === provider);
}

export function thinkingOn(model: string, want: Thinking, provider?: string): Thinking | null {
  const known = modelOf(model, provider);

  if (!known) return want === "off" ? "off" : null;
  if (want === "off") return known.mode === "always" ? null : "off";
  if (known.mode === "never") return null;
  if (want === "on") return known.efforts.length === 0 ? "on" : null;
  return known.efforts.includes(want) ? want : null;
}

export function theCheapestThatWillNotThink(provider: string): string | undefined {
  const spend = (model: AgentModel) => model.price.in + model.price.out;
  return modelsOfProvider(provider)
    .filter((model) => thinkingOn(model.id, "off", model.provider) !== null)
    .sort((one, two) => spend(one) - spend(two))[0]?.id;
}

export function thinkingFor(model: string, provider?: string): readonly Thinking[] {
  return THINKING.filter((level) => thinkingOn(model, level, provider) !== null);
}

export function settlesOn(model: string, provider?: string): Thinking {
  return modelOf(model, provider)?.settles ?? "off";
}

export function theDearestMillion(): { model: string; provider: string; dollars: number; seen: string } {
  const dearest = [...MODELS].sort((one, two) => two.price.out - one.price.out)[0];
  if (!dearest) throw new Error("the model table is empty, so nothing here has a price");
  return {
    model: dearest.id,
    provider: dearest.provider,
    dollars: dearest.price.out,
    seen: dearest.price.seen,
  };
}

export function costOf(model: string, used: Tokens, provider?: string): number | null {
  const known = modelOf(model, provider);
  if (!known) return null;

  const read = used.in + used.cacheRead + used.cacheWrite;
  const dollars = (read * known.price.in + used.out * known.price.out) / 1_000_000;
  return Math.round(dollars * A_DOLLAR);
}

const VENDOR_PREFIX = /^(zai-org|openai|anthropic|google|x-ai|xai|meta-llama|mistralai|deepseek-ai)-/;
const SHOUTED = new Set(["gpt", "glm", "o", "v", "k"]);

export function modelSaid(id: string): string {
  const parts = id.replace(VENDOR_PREFIX, "").split("-");
  const words: string[] = [];
  for (const part of parts) {
    const last = words[words.length - 1];
    if (/^\d+$/.test(part) && last !== undefined && /\d$/.test(last)) {
      words[words.length - 1] = `${last}.${part}`;
    } else if (/^[a-z]\d+$/.test(part)) {
      words.push(part.toUpperCase());
    } else if (SHOUTED.has(part)) {
      words.push(part.toUpperCase());
    } else if (/^\d/.test(part)) {
      words.push(part);
    } else {
      words.push(part.charAt(0).toUpperCase() + part.slice(1));
    }
  }
  return words.join(" ");
}
